import React, { PureComponent } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AsyncImage } from 'components/asyncImage';
import { ButtonIcon } from 'components/button';
import { I18n } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { formatBytes } from 'helpers/File';
import { DatahubEventsService } from 'modules/app/services';
import { TryEditVideoThumbnail } from 'modules/app/services/datahubEvents/media';
import { PlayerService } from 'modules/player';
import { PLAYER_STATUS_CATEGORIES } from 'modules/player/constants';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import {
  FILE_IMAGE_EXTENSIONS,
  MAX_THUMBNAIL_SIZE,
  FILE_EXCEPTIONS
} from '../../constants';
import FileUploadValidations from '../FileUploadValidations';

export default class FileVideoThumbnail extends PureComponent {
  fileRef = React.createRef();

  state = {
    thumbnailUrl: '',
    invalidExtension: false,
    invalidSize: false,
    thumbnailError: false,
    isThumbnailLoading: true
  };

  static propTypes = {
    mediaStatus: PropTypes.bool,
    mediaId: PropTypes.string,
    contentType: PropTypes.string,
    onChangeThumbnail: PropTypes.func,
    onSetThumbnail: PropTypes.func
  };

  componentDidUpdate() {
    if (this.state.thumbnailUrl) {
      this.props.onSetThumbnail?.(this.state.thumbnailUrl);
    }

    if (
      PLAYER_STATUS_CATEGORIES.THUMBNAIL_DONE.includes(
        this.props.mediaStatus
      ) &&
      !this.state.thumbnailUrl
    ) {
      this.setThumbnail(this.props.mediaId);
    }
  }

  componentWillUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  async setThumbnail(playerKey) {
    const maxAttempts = 3;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const attemptFetchThumbnail = async (attempt) => {
      try {
        const { url: thumbnailUrl } = await PlayerService.fetchThumbnail(
          playerKey
        );

        this.setState({
          thumbnailUrl,
          thumbnailError: false,
          isThumbnailLoading: false
        });
        this.props.onSetThumbnail?.(thumbnailUrl);
      } catch (_) {
        if (attempt < maxAttempts) {
          await delay(1000);
          attemptFetchThumbnail(attempt + 1);
          return;
        }
        this.setState({ thumbnailError: true, isThumbnailLoading: false });
      }
    };

    attemptFetchThumbnail(1);
  }

  checkException(file = {}) {
    let exception = null;

    if (file.type && !this.validateFileType(file.type)) {
      exception = FILE_EXCEPTIONS.INVALID_EXTENSION;
    } else if (file.size && !this.validateFileSize(file.size)) {
      exception = FILE_EXCEPTIONS.BIG_FILE;
    }

    return exception;
  }

  clearException = () => {
    this.fileRef.current.value = null;
    this.setState({ exception: null });
  };

  validateFileType(fileExtension) {
    return FILE_IMAGE_EXTENSIONS.includes(fileExtension);
  }

  validateFileSize(fileSize) {
    return MAX_THUMBNAIL_SIZE >= fileSize;
  }

  onThumbnailError = () =>
    this.setState({ thumbnailError: true, isThumbnailLoading: false });

  onThumbnailLoad = () =>
    this.setState({ isThumbnailLoading: false, thumbnailError: false });

  renderDefaultThumbnail = () => {
    return (
      <div className="file__content__thumbnail__image file__content__thumbnail__placeholder">
        <FontAwesomeIcon icon={['fal', 'image']} />
      </div>
    );
  };

  renderThumbnail = () => {
    const { thumbnailUrl, isThumbnailLoading } = this.state;

    const thumbnailClass = classNames('file__content__thumbnail__image', {
      'file__content__thumbnail__image--success': !isThumbnailLoading
    });

    return (
      <AsyncImage
        isThumbnailLoading={isThumbnailLoading}
        onStopLoading={() => this.setState({ isThumbnailLoading: false })}
        url={thumbnailUrl}
        className={thumbnailClass}
        onError={this.onThumbnailError}
        onLoad={this.onThumbnailLoad}
      />
    );
  };

  render() {
    const { exception, thumbnailError } = this.state;

    return (
      <FileUploadValidations
        exception={exception}
        resetException={this.clearException}
        maxFileSize={MAX_THUMBNAIL_SIZE}
      >
        <label className="mt__0">
          <I18n label="player_section.thumbnail_title" />
        </label>
        <div className="file__content__thumbnail">
          {thumbnailError
            ? this.renderDefaultThumbnail()
            : this.renderThumbnail()}

          <div className="file__content__thumbnail__change">
            <label className="label__description">
              <I18n
                label="player_section.thumbnail_warning"
                data={{ fileSize: formatBytes(MAX_THUMBNAIL_SIZE) }}
              />
            </label>

            <input
              type="file"
              ref={this.fileRef}
              style={{ display: 'none' }}
              onChange={this.previewImage}
            />
            <ButtonIcon icon="change" transparent onClick={this.onClick}>
              <I18n label="general.change_image" />
            </ButtonIcon>
          </div>
        </div>
      </FileUploadValidations>
    );
  }

  previewImage = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();

    const exception = this.checkException(file);

    if (!exception) {
      this.uploadImage(file);

      reader.addEventListener('load', () => {
        const image = reader.result;

        this.setState({
          thumbnailUrl: image,
          invalidExtension: false,
          invalidSize: false
        });

        this.props.onSetThumbnail?.(image);
      });

      if (file) {
        reader.readAsDataURL(file);
      }
    } else {
      this.setState({ exception, maxFileSize: MAX_THUMBNAIL_SIZE });
    }
  };

  onChangeThumbnail = ({ loading }) => {
    const { onChangeThumbnail } = this.props;

    onChangeThumbnail && onChangeThumbnail({ loading });
  };

  uploadImage = async (file) => {
    const { mediaId } = this.props;
    let statusCode = HttpCodes.OK;
    let errorMessage = 'NOT_FAILURE_RESULT_EVENT';

    this.onChangeThumbnail({ loading: true });

    try {
      await PlayerService.uploadThumbnail(mediaId, file);
      UiService.setAlertSuccess('change_image_success');
      this.onChangeThumbnail({ loading: false });
    } catch (error) {
      UiService.setAlertError('change_image_error');
      statusCode = error.status;
      errorMessage = error.message;
    }

    DatahubEventsService.sendEvent({
      schema: new TryEditVideoThumbnail({
        statusCode,
        errorMessage,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  };

  onClick = () => {
    this.fileRef.current.click();
  };
}
